import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getTopPodcasts,
  searchPodcasts,
  getPodcastsByGenre,
  getPodcastReleaseDate,
  getGenres,
  getTopPodcastsForDemo,
  getCountries,
  getCachedData,
} from '../../api/podcasts';
import moment from 'moment';
import { getFavorites, saveFavorites } from '../../utils/favorites';
import { getPodcastIdFromUrl } from '../../utils/updatePodcastUrl';

const podId = getPodcastIdFromUrl();
const isNumericPodId = podId && /^\d+$/.test(podId);

export const fetchPodcasts = createAsyncThunk('podcasts/fetchPodcasts', async (params, { getState }) => {
  const { activePlatform } = getState().podcasts;
  const source = activePlatform === 'apple' ? '' : activePlatform;
  const response = await searchPodcasts({ ...params, ...(source ? { source } : {}) });
  return response;
});

export const fetchTopPodcasts = createAsyncThunk('podcasts/fetchTopPodcasts', async (params, { getState }) => {
  const { activePlatform } = getState().podcasts;
  const source = activePlatform === 'apple' ? '' : activePlatform;
  const response = await getTopPodcasts({ ...params, ...(source ? { source } : {}) });
  return response;
});

export const fetchTopPodcastsForDemo = createAsyncThunk(
  'podcasts/fetchTopPodcastsForDemo',
  async (params, { getState }) => {
    const { activePlatform } = getState().podcasts;
    const source = activePlatform === 'apple' ? '' : activePlatform;
    const response = await getTopPodcastsForDemo({ ...params, ...(source ? { source } : {}) });
    return response;
  }
);

export const fetchPodcastsByGenre = createAsyncThunk('podcasts/fetchPodcastsByGenre', async (params, { getState }) => {
  const { activePlatform } = getState().podcasts;
  const source = activePlatform === 'apple' ? '' : activePlatform;
  const response = await getPodcastsByGenre({ ...params, ...(source ? { source } : {}) });
  return response;
});

export const fetchPodcastReleaseDate = createAsyncThunk(
  'podcasts/fetchPodcastReleaseDate',
  async (params) => {
    const response = await getPodcastReleaseDate(params);
    return response;
  }
);

export const fetchGenres = createAsyncThunk('podcasts/fetchGenres', async (params, { getState }) => {
  const { activePlatform } = getState().podcasts;
  const source = activePlatform === 'apple' ? '' : activePlatform;
  const response = await getGenres({ ...params, ...(source ? { source } : {}) });
  return response;
});

export const fetchCountries = createAsyncThunk('podcasts/fetchCountries', async (params, { getState }) => {
  const { activePlatform } = getState().podcasts;
  const source = activePlatform === 'apple' ? '' : activePlatform;
  const response = await getCountries({ ...params, ...(source ? { source } : {}) });
  return response;
});

export const loadPodcastCharts = createAsyncThunk(
  'podcasts/loadPodcastCharts',
  async (podcast, { dispatch, getState }) => {
    const { activePlatform } = getState().podcasts;
    const source = activePlatform === 'apple' ? '' : activePlatform;
    const response = await dispatch(fetchPodcasts({ ...podcast, ...(source ? { source } : {}) })).unwrap();
    const foundPodcast = response.find(
      (item) => item.collectionName.toLowerCase() === podcast.name.toLowerCase()
    );

    dispatch(
      fetchTopPodcasts({
        name: foundPodcast.collectionName,
        artist: foundPodcast.artistName,
        startDate: moment().subtract(30, 'days').format(),
        endDate: moment().format(),
        source
      })
    );

    return foundPodcast;
  }
);

export const setFavorites = createAsyncThunk(
  'podcasts/setFavorites',
  async ({ podcastId, name }, { getState }) => {
    const { favorites } = getState().podcasts;
    const newFavorites = favorites.some((fav) => fav.podcastId === podcastId)
      ? favorites.filter((fav) => fav.podcastId !== podcastId)
      : [...favorites, { podcastId, name }];

    saveFavorites(newFavorites);

    return newFavorites;
  }
);

export const fetchCachedPodcast = createAsyncThunk('podcasts/fetchCachedPodcast', async () => {
  const response = await getCachedData();
  return response;
});

const initialState = {
  podcasts: {
    data: [],
    loading: false,
    error: null,
  },
  allGenres: {
    data: [],
    loading: false,
    error: null,
  },
  allCountries: {
    data: [],
    loading: false,
    error: null,
  },
  demoModePodcasts: [],
  podcastsByGenre: {},
  currentPodcast: null,
  selectedGenre: null,
  selectedCountry: 'United States',
  startDate: moment().subtract(60, 'days').format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
  status: null,
  error: null,
  episodes: [],
  topPodcasts: {
    data: [],
    loading: false,
    error: null,
  },
  genres: [],
  hiddenGenres: [],
  browseMode: false,
  browseModeGenre: 'Top Podcasts',
  browseModeCountry: 'United States',
  showBrowseGrid: false,
  favorites: getFavorites(),
  isOnlyFavoritesForDemo: false,
  shouldFetchTopPodcasts: false,
  activePlatform: !podId || isNumericPodId ? 'apple' : 'spotify',
  spotifyDemoPodcastsAviableGenre: '',
  showAnalyze: false,
};

export const podcastsSlice = createSlice({
  name: 'podcasts',
  initialState,
  reducers: {
    setSpotifyDemoPodcastsAviableGenre: (state, action) => {
      state.spotifyDemoPodcastsAviableGenre = action.payload;
    },
    setShouldFetchTopPodcasts: (state, action) => {
      state.shouldFetchTopPodcasts = action.payload;
    },
    setCurrentPodcast: (state, action) => {
      state.currentPodcast = action.payload;
    },
    setSelectedGenre: (state, action) => {
      state.selectedGenre = action.payload;
    },
    resetPodcasts: (state) => {
      state.podcasts.data = [];
      state.episodes = [];
      state.topPodcasts = {
        data: [],
        loading: false,
        error: null,
      };
    },
    resetTopPodcasts: (state) => {
      state.topPodcasts = initialState.topPodcasts;
    },
    resetLoadingsOnDemoStop: (state) => {
      state.topPodcasts.loading = false;
      state.podcasts.loading = false;
    },
    resetDemoModePodcasts: (state) => {
      state.demoModePodcasts = [];
    },
    setSelectedCountry: (state, action) => {
      state.selectedCountry = action.payload;
    },
    resetPodcastsByGenre: (state) => {
      state.podcastsByGenre = {};
    },
    setDateRange: (state, action) => {
      const { startDate, endDate } = action.payload;
      state.startDate = startDate;
      state.endDate = endDate;
    },
    resetDateRange: (state) => {
      const defaultDays = window.innerWidth <= 600 ? 7 : 30;
      state.startDate = moment().subtract(defaultDays, 'days').format();
      state.endDate = moment().format();
    },
    setGenres: (state, action) => {
      state.genres = action.payload;
    },
    toggleHiddenGenre: (state, action) => {
      const genre = action.payload;
      const index = state.hiddenGenres.indexOf(genre);
      if (index === -1) {
        state.hiddenGenres.push(genre);
      } else {
        state.hiddenGenres.splice(index, 1);
      }
    },
    resetGenres: (state) => {
      state.genres = [];
      state.hiddenGenres = [];
    },
    setBrowseMode: (state, action) => {
      state.browseMode = action.payload;
    },
    setBrowseModeGenre: (state, action) => {
      state.browseModeGenre = action.payload;
    },
    setBrowseModeCountry: (state, action) => {
      state.browseModeCountry = action.payload;
    },
    setShowBrowseGrid: (state, action) => {
      state.showBrowseGrid = action.payload;
    },
    resetBrowseMode: (state) => {
      state.browseModeCountry = initialState.browseModeCountry;
      state.browseModeGenre = initialState.browseModeGenre;
    },
    setOnlyFavoritesForDemo: (state, action) => {
      state.isOnlyFavoritesForDemo = action.payload;
    },
    setActivePlatform: (state, action) => {
      state.activePlatform = action.payload;
    },
    setShowAnalyze: (state, action) => {
      state.showAnalyze = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPodcasts.pending, (state) => {
        state.podcasts.loading = true;
      })
      .addCase(fetchPodcasts.fulfilled, (state, action) => {
        state.podcasts.loading = false;
        state.podcasts.data = action.payload;
      })
      .addCase(fetchPodcasts.rejected, (state, action) => {
        state.podcasts.loading = false;
        state.podcasts.error = action.error.message;
      })
      .addCase(fetchTopPodcasts.pending, (state) => {
        state.topPodcasts.loading = true;
      })
      .addCase(fetchTopPodcasts.fulfilled, (state, action) => {
        state.topPodcasts.loading = false;
        state.topPodcasts.data = action.payload.topPodcasts;
        state.episodes = action.payload.episodes;
      })
      .addCase(fetchTopPodcasts.rejected, (state, action) => {
        state.topPodcasts.loading = false;
        state.topPodcasts.error = action.error.message;
      })
      .addCase(fetchPodcastsByGenre.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchPodcastsByGenre.fulfilled, (state, action) => {
        state.status = 'success';
        const genre = action.meta.arg.genre;
        state.podcastsByGenre[genre] = action.payload;
      })
      .addCase(fetchPodcastsByGenre.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.error.message;
      })
      .addCase(loadPodcastCharts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loadPodcastCharts.fulfilled, (state, action) => {
        state.status = 'success';
        state.currentPodcast = action.payload;
      })
      .addCase(loadPodcastCharts.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.error.message;
      })
      .addCase(fetchTopPodcastsForDemo.pending, (state) => {
        state.podcasts.loading = true;
      })
      .addCase(fetchTopPodcastsForDemo.fulfilled, (state, action) => {
        state.podcasts.loading = false;
        state.demoModePodcasts = action.payload.topPodcasts;
      })
      .addCase(fetchTopPodcastsForDemo.rejected, (state, action) => {
        state.podcasts.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchGenres.pending, (state) => {
        state.allGenres.loading = true;
      })
      .addCase(fetchGenres.fulfilled, (state, action) => {
        state.allGenres.loading = false;
        state.allGenres.data = action.payload;
      })
      .addCase(fetchGenres.rejected, (state, action) => {
        state.allGenres.loading = false;
        state.allGenres.error = action.error.message;
      })
      .addCase(fetchCountries.pending, (state) => {
        state.allCountries.loading = true;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.allCountries.loading = false;
        state.allCountries.data = action.payload;
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.allCountries.loading = false;
        state.allCountries.error = action.error.message;
      })
      .addCase(setFavorites.fulfilled, (state, action) => {
        state.favorites = action.payload;
      })
      .addCase(fetchCachedPodcast.pending, (state) => {
        state.topPodcasts.loading = true;
      })
      .addCase(fetchCachedPodcast.fulfilled, (state, action) => {
        state.topPodcasts.loading = false;
        state.demoModePodcasts = action.payload.demoModePodcasts;
        state.currentPodcast = action.payload.currentPodcast;
        state.topPodcasts.data = action.payload.topPodcasts;
        state.episodes = action.payload.episodes;
      })
      .addCase(fetchCachedPodcast.rejected, (state, action) => {
        state.topPodcasts.loading = false;
        state.error = action.error.message;
      });
  },
});

export const {
  setCurrentPodcast,
  resetPodcasts,
  setSelectedGenre,
  setSelectedCountry,
  resetPodcastsByGenre,
  setDateRange,
  resetDateRange,
  setGenres,
  toggleHiddenGenre,
  resetGenres,
  setBrowseMode,
  setBrowseModeGenre,
  setBrowseModeCountry,
  setShowBrowseGrid,
  resetBrowseMode,
  resetDemoModePodcasts,
  setOnlyFavoritesForDemo,
  setShouldFetchTopPodcasts,
  setActivePlatform,
  resetTopPodcasts,
  resetLoadingsOnDemoStop,  
  setSpotifyDemoPodcastsAviableGenre,
  setShowAnalyze,
} = podcastsSlice.actions;

export default podcastsSlice.reducer;
